import { createReducer, combineReducersWithRoot } from '@common/redux-utils';
import {
  AuthActionType,
  GetCvisStartAction,
  GetCvisSuccessAction,
  GetCvisFailureAction,
  GetAuthReportSummaryStart,
  GetAuthReportSummarySuccess,
  GetAuthReportSummaryFailure,
  GetContractCviCostHistoryStartAction,
  GetContractCviCostHistorySuccessAction,
  GetContractCviCostHistoryFailureAction,
  SetSelectedCviAction,
  // CreatePurchaseRequestComponentStartAction,
  // CreatePurchaseRequestComponentSuccessAction,
  // CreatePurchaseRequestComponentFailureAction,
  SetReportIdAction,
  GetContractCostHistoryStartAction,
  GetContractCostHistorySuccessAction,
  GetContractCostHistoryFailureAction,
  SaveAuthoDiscountAction,
  GetAuthoHistoryStartAction,
  GetAuthoHistorySuccessAction,
  GetAuthoHistoryFailureAction,
  GetPurchaseRequestsAction
} from './authorization.actions';

import { SwoCviViewModel } from '@models/AuthorizationSwoCvi';
import { AuthReportSummaryViewModel, SwoAuthoReportSummarySearchViewModel } from '@models/AuthReportSummary';
import { ContractCviCostHistoryViewModel } from '@models/ContractCviCostHistoryViewModel';

import {
  AuthorizationSearchReducer,
  AuthorizationSearchState,
  initialAuthorizationSearchState
} from './_components/authorization-search/authorization-search.reducer';
import {
  AuthorizationFormState,
  initialAuthorizationFormState,
  AuthorizationFormReducer
} from './authorization-form.reducer';
import { SaveAuthorizationFormProgressAction, SetChargeBreakdownsAction } from './authorization.actions';
import { AuthorizationChargeBreakdown } from '@models/AuthorizationChargeBreakdown';
import { ContractTotalCostHistoryViewModel } from '@models/ServiceWorkOrder';
import { PurchaseRequestSimpleViewModel } from '@models/PurchaseRequest';

export interface AuthorizationState {
  readonly authChargeBreakdowns: Array<AuthorizationChargeBreakdown>;
  readonly authList: Array<SwoAuthoReportSummarySearchViewModel>;
  readonly authReportSummary: AuthReportSummaryViewModel | null;
  readonly contractCostHistory: Array<ContractTotalCostHistoryViewModel> | null;
  readonly costHistoryLoading: boolean;
  readonly contractCviCostHistory: ContractCviCostHistoryViewModel | null;
  readonly cvis: Array<SwoCviViewModel>;
  readonly selectedCvi: SwoCviViewModel | null;
  readonly query: string | null;
  readonly filterBy: string | null;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly pageCount: number;
  readonly orderBy: string;
  readonly message: string;
  readonly loading?: boolean;
  readonly form: AuthorizationFormState;
  readonly search: AuthorizationSearchState;
  readonly reportId: number; // id of the summary / report that already exists
  readonly overrideDiscount: number;
  readonly buildingPermitHistory?: number;
  readonly codeUpgradeHistory?: number;
  readonly purchaseRequests?: Array<PurchaseRequestSimpleViewModel>;
}

export const initialAuthorizationState: AuthorizationState = {
  authList: [] as Array<SwoAuthoReportSummarySearchViewModel>,
  authReportSummary: null,
  contractCostHistory: null,
  costHistoryLoading: false,
  contractCviCostHistory: null,
  cvis: [] as Array<SwoCviViewModel>,
  selectedCvi: null,
  query: '',
  filterBy: '',
  pageNumber: 0,
  pageSize: 10,
  pageCount: 10,
  orderBy: '',
  message: '',
  search: initialAuthorizationSearchState,
  form: initialAuthorizationFormState,
  reportId: 0,
  overrideDiscount: 0,
  authChargeBreakdowns: []
};

function handleGetCvisStart(state: AuthorizationState, action: GetCvisStartAction): AuthorizationState {
  return {
    ...state,
    message: action.type
  };
}

function handleGetCvisSuccess(state: AuthorizationState, action: GetCvisSuccessAction): AuthorizationState {
  return {
    ...state,
    cvis: action.cvis,
    selectedCvi: action.currentCvi
  };
}

function handleGetCvisFailure(state: AuthorizationState, action: GetCvisFailureAction): AuthorizationState {
  return {
    ...state,
    message: action.errorMessage
  };
}

function handleGetAuthReportSummaryStart(
  state: AuthorizationState,
  action: GetAuthReportSummaryStart
): AuthorizationState {
  return {
    ...state,
    message: action.type
  };
}

function handleGetAuthReportSummarySuccess(
  state: AuthorizationState,
  action: GetAuthReportSummarySuccess
): AuthorizationState {
  return {
    ...state,
    authReportSummary: action.authReportSummary
  };
}

function handleGetAuthReportSummaryFailure(
  state: AuthorizationState,
  action: GetAuthReportSummaryFailure
): AuthorizationState {
  return {
    ...state,
    message: action.errorMessage
  };
}

function handleGetContractCviCostHistoryStart(
  state: AuthorizationState,
  action: GetContractCviCostHistoryStartAction
): AuthorizationState {
  return {
    ...state,
    message: action.type
  };
}

function handleGetContractCviCostHistorySuccess(
  state: AuthorizationState,
  action: GetContractCviCostHistorySuccessAction
): AuthorizationState {
  return {
    ...state,
    contractCviCostHistory: action.contractCviCostHistory
  };
}

function handleGetContractCviCostHistoryFailure(
  state: AuthorizationState,
  action: GetContractCviCostHistoryFailureAction
): AuthorizationState {
  return {
    ...state,
    message: action.errorMessage
  };
}

function handleSetSelectedCvi(state: AuthorizationState, action: SetSelectedCviAction): AuthorizationState {
  return {
    ...state,
    selectedCvi: action.selectedCvi
  };
}
function handleAuthoDiscountSave(state: AuthorizationState, action: SaveAuthoDiscountAction): AuthorizationState {
  return {
    ...state,
    overrideDiscount: action.discount
  };
}

function handleSaveAuthorizationFormProgress(
  state: AuthorizationState,
  action: SaveAuthorizationFormProgressAction
): AuthorizationState {
  return {
    ...state
  };
}
// function handleCreatePurchaseRequestStart(
//   state: AuthorizationState,
//   action: CreatePurchaseRequestComponentStartAction
// ): AuthorizationState {
//   return {
//     ...state,
//     message: action.type
//   };
// }

// function handleCreatePurchaseRequestSuccess(
//   state: AuthorizationState,
//   action: CreatePurchaseRequestComponentSuccessAction
// ): AuthorizationState {
//   return {
//     ...state
//   };
// }

// function handleCreatePurchaseRequestFailure(
//   state: AuthorizationState,
//   action: CreatePurchaseRequestComponentFailureAction
// ): AuthorizationState {
//   return {
//     ...state,
//     message: action.errorMessage
//   };
// }

function handleSetReportId(state: AuthorizationState, action: SetReportIdAction) {
  return {
    ...state,
    reportId: action.id
  };
}

function handleGetContractCostHistoryStart(
  state: AuthorizationState,
  action: GetContractCostHistoryStartAction
): AuthorizationState {
  return {
    ...state,
    costHistoryLoading: true,
    message: action.type
  };
}
function handleGetContractCostHistorySuccess(
  state: AuthorizationState,
  action: GetContractCostHistorySuccessAction
): AuthorizationState {
  return {
    ...state,
    contractCostHistory: action.costHistory,
    costHistoryLoading: false
  };
}
function handleGetContractCostHistoryFailure(
  state: AuthorizationState,
  action: GetContractCostHistoryFailureAction
): AuthorizationState {
  return {
    ...state,
    message: action.message
  };
}

function handleGetAuthoHistoryStart(state: AuthorizationState, action: GetAuthoHistoryStartAction): AuthorizationState {
  return {
    ...state,
    message: action.type
  };
}
function handleGetAuthoHistorySuccess(
  state: AuthorizationState,
  action: GetAuthoHistorySuccessAction
): AuthorizationState {
  return {
    ...state,
    codeUpgradeHistory: action.codeUpgradeHistory,
    buildingPermitHistory: action.buildingPermitHistory
  };
}
function handleGetAuthoHistoryFailure(
  state: AuthorizationState,
  action: GetAuthoHistoryFailureAction
): AuthorizationState {
  return {
    ...state,
    message: action.message
  };
}

function handleSetChargeBreakDowns(state: AuthorizationState, action: SetChargeBreakdownsAction): AuthorizationState {
  return {
    ...state,
    authChargeBreakdowns: action.breakdowns
  };
}

function handleSetPurchaseRequests(state: AuthorizationState, action: GetPurchaseRequestsAction): AuthorizationState {
  return {
    ...state,
    purchaseRequests: action.requests
  };
}

const reducer = createReducer<AuthorizationState, AuthActionType>(initialAuthorizationState, {
  [AuthActionType.GetCvisStart]: handleGetCvisStart,
  [AuthActionType.GetCvisSuccess]: handleGetCvisSuccess,
  [AuthActionType.GetCvisFailure]: handleGetCvisFailure,

  [AuthActionType.GetAuthReportSummaryStart]: handleGetAuthReportSummaryStart,
  [AuthActionType.GetAuthReportSummarySuccess]: handleGetAuthReportSummarySuccess,
  [AuthActionType.GetAuthReportSummaryFailure]: handleGetAuthReportSummaryFailure,

  [AuthActionType.GetContractCviCostHistoryStart]: handleGetContractCviCostHistoryStart,
  [AuthActionType.GetContractCviCostHistorySuccess]: handleGetContractCviCostHistorySuccess,
  [AuthActionType.GetContractCviCostHistoryFailure]: handleGetContractCviCostHistoryFailure,

  [AuthActionType.SetSelectedCvi]: handleSetSelectedCvi,
  [AuthActionType.SaveAuthorizationFormProgress]: handleSaveAuthorizationFormProgress,
  [AuthActionType.SaveAuthoDiscount]: handleAuthoDiscountSave,

  // [AuthActionType.CreatePurchaseRequestStart]: handleCreatePurchaseRequestStart,
  // [AuthActionType.CreatePurchaseRequestSuccess]: handleCreatePurchaseRequestSuccess,
  // [AuthActionType.CreatePurchaseRequestFailure]: handleCreatePurchaseRequestFailure,
  [AuthActionType.SetReportId]: handleSetReportId,

  [AuthActionType.GetContractCostHistoryStart]: handleGetContractCostHistoryStart,
  [AuthActionType.GetContractCostHistorySuccess]: handleGetContractCostHistorySuccess,
  [AuthActionType.GetContractCostHistoryFailure]: handleGetContractCostHistoryFailure,

  [AuthActionType.GetAuthoHistoryStart]: handleGetAuthoHistoryStart,
  [AuthActionType.GetAuthoHistorySuccess]: handleGetAuthoHistorySuccess,
  [AuthActionType.GetAuthoHistoryFailure]: handleGetAuthoHistoryFailure,

  [AuthActionType.SetChargeBreakdowns]: handleSetChargeBreakDowns,

  [AuthActionType.SetPurchaseRequests]: handleSetPurchaseRequests
});

export const AuthorizationReducer = combineReducersWithRoot(reducer, {
  search: AuthorizationSearchReducer,
  form: AuthorizationFormReducer
});



// WEBPACK FOOTER //
// ./src/authorization/authorization.reducer.ts